import { Link } from 'react-router-dom';
import categories from '@/mocks/categories';

export interface Article {
  id: string;
  slug: string;
  title: string;
  excerpt: string;
  category: string;
  tags: string[];
  cover: string;
  author: string;
  date: string;
  readTime: string;
  heat: number;
  featured?: boolean;
}

interface ArticleCardProps {
  article: Article;
  layout?: 'vertical' | 'horizontal';
}

export function categoryName(slug: string) {
  const found = categories.find((c) => c.slug === slug);
  return found ? found.name : '甜味瓜';
}

export default function ArticleCard({ article, layout = 'vertical' }: ArticleCardProps) {
  if (layout === 'horizontal') {
    return (
      <Link
        to={`/article/${article.slug}`}
        className="group flex gap-4 rounded-xl border border-background-200 bg-background-50 p-3 hover:border-primary-300 transition-colors cursor-pointer"
      >
        <div className="w-32 h-24 md:w-40 md:h-28 shrink-0 overflow-hidden rounded-lg bg-background-200">
          <img
            src={article.cover}
            alt={article.title}
            title={`${article.title} - 糖心吃瓜`}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          />
        </div>
        <div className="flex flex-col justify-between min-w-0 py-1">
          <div>
            <span className="text-xs text-primary-600">{categoryName(article.category)}</span>
            <h3 className="mt-1 text-sm md:text-base font-medium text-foreground-950 leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors">
              {article.title}
            </h3>
            <p className="mt-1.5 hidden sm:block text-xs text-foreground-600 line-clamp-2 leading-relaxed">
              {article.excerpt}
            </p>
          </div>
          <div className="mt-2 flex items-center gap-3 text-xs text-foreground-500">
            <span>{article.date}</span>
            <span className="flex items-center gap-1">
              <i className="ri-fire-line" aria-hidden="true"></i>
              {article.heat}
            </span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${article.slug}`}
      className="group flex flex-col overflow-hidden rounded-xl border border-background-200 bg-background-50 hover:border-primary-300 transition-all duration-300 hover:-translate-y-1 cursor-pointer"
    >
      <div className="w-full h-44 overflow-hidden bg-background-200">
        <img
          src={article.cover}
          alt={article.title}
          title={`${article.title} - 糖心吃瓜`}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
      </div>
      <div className="flex flex-1 flex-col p-4 md:p-5">
        <div className="flex items-center gap-2">
          <span className="rounded-full bg-secondary-100 px-2.5 py-0.5 text-xs text-secondary-800">
            {categoryName(article.category)}
          </span>
          <span className="text-xs text-foreground-500">{article.readTime}</span>
        </div>
        <h3 className="mt-3 text-base font-medium text-foreground-950 leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors">
          {article.title}
        </h3>
        <p className="mt-2 flex-1 text-sm text-foreground-600 leading-relaxed line-clamp-2">
          {article.excerpt}
        </p>
        <div className="mt-4 flex items-center justify-between border-t border-background-100 pt-3 text-xs text-foreground-500">
          <span className="flex items-center gap-1.5">
            <i className="ri-user-smile-line text-primary-400" aria-hidden="true"></i>
            {article.author}
          </span>
          <span className="flex items-center gap-1">
            <i className="ri-fire-line" aria-hidden="true"></i>
            {article.heat}
          </span>
        </div>
      </div>
    </Link>
  );
}